// lib/services/compute/animation_library_pool.ts

import TypedSlotPool, { PostReleasePolicy } from "./typed_slot_pool";
import AnimationLibrarySlot from "./animation_library_slot";
import CharacterBuilderGpu from "./character_builder_gpu";

/**
 * Single-slot pool wrapping the static GPU library buffers.
 *
 * The slot is permanent_ready: the pool always returns the same slot
 * from acquire_latest_ready_slot without any lease bookkeeping. Multiple
 * callers within the same frame may "acquire" the slot concurrently
 * because static data is read-only.
 */
export default class AnimationLibraryPool {
  private pool: TypedSlotPool<AnimationLibrarySlot>;
  private slot: AnimationLibrarySlot | null = null;
  private initialized = false;

  constructor() {
    this.pool = new TypedSlotPool<AnimationLibrarySlot>(
      PostReleasePolicy.KeepReady
    );
  }

  initialize(): boolean {
    if (this.initialized) return true;

    this.slot = new AnimationLibrarySlot();
    this.pool.initialize([this.slot]);
    this.initialized = true;
    return true;
  }

  shutdown(): void {
    if (this.slot) {
      this.slot.clear_references();
    }
    this.pool.shutdown();
    this.slot = null;
    this.initialized = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * One-time population of the static library slot. After this call
   * the slot is permanently Ready and acquire_latest_ready_slot will
   * return it for the rest of the pool's lifetime.
   */
  populate(builder: CharacterBuilderGpu): boolean {
    if (!this.initialized || !this.slot) return false;
    this.slot.populate_from(builder);
    return true;
  }

  /**
   * Returns the static library slot. No lease tracking for permanent slots;
   * release_latest_ready_slot is a no-op. Safe to call multiple times.
   */
  acquire_latest_ready_slot(): AnimationLibrarySlot | null {
    return this.pool.acquire_latest_ready_slot();
  }

  release_latest_ready_slot(): void {
    this.pool.release_leased_slot();
  }

  getSlotCount(): number {
    return this.pool.getSlotCount();
  }
}